import React, { useState } from 'react';
import { usePageData } from '../../contexts/DataContext';
import { getApiUrl } from '../../utils/environment';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: contactPage } = usePageData('contactPage');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error message when user starts typing
    if (submitStatus.type === 'error') {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all fields' });
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }
    
    // Message length validation
    if (formData.message.trim().length < 10) {
      setSubmitStatus({ type: 'error', message: 'Message must be at least 10 characters long' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(getApiUrl('/contact/submit/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: data.message || 'Message sent successfully! We will get back to you soon.' 
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus({ type: '', message: '' });
        }, 5000);
      } else {
        // Handle validation errors from backend
        let errorMessage = 'Failed to send message. Please try again.';
        
        if (data.detail) {
          errorMessage = data.detail;
        } else if (data.name || data.email || data.subject || data.message) {
          errorMessage = Object.values(data).flat().join(', ');
        }
        
        setSubmitStatus({ type: 'error', message: errorMessage });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section">
      <div className="container">
        <h2>{contactPage?.title || 'Contact Us'}</h2>
        <p>{contactPage?.subtitle || "We'd love to hear from you! Reach out for support, partnerships, or just to say hello."}</p>

        <div className="contact-form">
          <h3>{contactPage?.form_title || 'Send Us a Message'}</h3>
          <div className="underline"></div>

          <form onSubmit={handleSubmit}>
            <input 
              type="text" 
              name="name" 
              placeholder="Your Name" 
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              required 
            />
            <input 
              type="email" 
              name="email" 
              placeholder="Email" 
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              required 
            />
            <select 
              name="subject" 
              className="subject"
              value={formData.subject}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            >
              <option value="">Subject</option>
              <option value="support">Support</option>
              <option value="partnership">Partnership</option>
              <option value="feedback">Feedback</option>
              <option value="other">Other</option>
            </select>
            <textarea 
              name="message" 
              placeholder="Your Message" 
              rows="6"
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            ></textarea>
            
            {submitStatus.message && (
              <div className={`form-message ${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}
            
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;